import { Link } from 'react-router-dom';
import styled, { css } from 'styled-components';

import { styledReset } from '@pkg/primitives';

const actionPaddingDesktop = 6;
const actionWrapperOffsetPx = `-${actionPaddingDesktop}px`;

interface StyledSettingsActionProps {
    $destructive?: boolean;
    $fullWidth?: boolean;
    $slim?: boolean;
}

const actionBase = css`
    display: block;
    position: relative;
    padding: var(--yat_settings-action-padding);
    width: var(--var_settings-action-width);
    text-align: var(--yat_settings-action-alignment);
    color: var(--yat_settings-action-color);
    background-color: var(--yat_settings-action-bg-color);
    border-radius: var(--yat_settings-action-radius);
    transition-property: background-color;
    transition-duration: ${({ theme }) => theme.transitions.speeds.fast}s;
    transition-timing-function: ${({ theme }) => theme.transitions.easesCss.cubic};

    &:hover {
        background-color: ${({ theme }) => theme.shades.white['7']};
    }

    &:active {
        background-color: ${({ theme }) => theme.shades.white['4']};
    }

    ///
    /// Focus ring

    &::before {
        content: '';
        position: absolute;
        top: -4px;
        right: -4px;
        bottom: -4px;
        left: -4px;
        margin: auto;
        display: block;
        border: 2px solid ${({ theme }) => theme.colors.purple};
        border-radius: calc(var(--yat_settings-action-radius) + 3px);
        opacity: 0;
        transition-property: opacity;
        transition-duration: ${({ theme }) => theme.transitions.speeds.fast}s;
        transition-timing-function: ${({ theme }) => theme.transitions.easesCss.cubic};
    }

    // Unforuntately, not all browsers treat ':focus'
    // the same for <a /> elements.
    &:focus-visible::before {
        opacity: 1;
    }
`;

export const StyledSettingsActionGroup = styled.div<{ $slim?: boolean }>`
    display: grid;
    gap: 4px;
    justify-items: ${({ $slim }) => ($slim ? 'start' : 'normal')};
    margin: ${({ $slim }) => ($slim ? actionWrapperOffsetPx : 0)};
`;

export const StyledSettingsAction = styled.div<StyledSettingsActionProps>`
    --yat_settings-action-padding: ${({ $slim }) => ($slim ? `${actionPaddingDesktop}px` : '16px')};
    --var_settings-action-width: ${({ $fullWidth }) => ($fullWidth ? '100%' : 'auto')};
    --yat_settings-action-alignment: ${({ $slim }) => ($slim ? 'left' : 'center')};
    --yat_settings-action-font-size: ${({ $slim }) => ($slim ? '16px' : '18px')};
    --yat_settings-action-color: ${(props) => (props.$destructive ? props.theme.colors.red : props.theme.colors.white)};
    --yat_settings-action-bg-color: transparent;
    --yat_settings-action-radius: ${({ $slim }) => ($slim ? '4px' : '64px')};
`;

export const ButtonAction = styled.button.attrs({ type: 'button' })`
    && {
        ${styledReset.button}
        ${actionBase}
    }

    &&:disabled {
        cursor: not-allowed;
        color: ${(props) => props.theme.colors.grey};
    }
`;

export const LinkAction = styled(Link)`
    && {
        ${styledReset.link}
        ${actionBase}
    }
`;

export const Content = styled.span`
    ${styledReset.typography}
    display: block;
    font-family: 'Alliance No.1';
    font-size: var(--yat_settings-action-font-size);
    font-weight: 700;
    line-height: 1;
    color: currentColor;
`;
